import { Injectable } from '@angular/core';
import {
    ERROR_REPORTER_BREADCRUMB_TYPE,
    ErrorReporterBreadcrumbType,
    ErrorReporterMetadata,
} from '@app/common/observability/domain/services/error-reporter.service';
import {
    BREADCRUMB_NAME,
    BreadcrumbName,
} from '@app/common/observability/domain/services/breadcrumb-name.catalog';
import { IBreadcrumbTrackerService } from '@app/common/observability/domain/services/breadcrumb-tracker.service';
import { ErrorReporterService } from '../../services/error-reporter.service';

@Injectable({
    providedIn: 'root',
})
export class BugsnagBreadcrumbTrackerService implements IBreadcrumbTrackerService {
    public constructor(private readonly errorReporterService: ErrorReporterService) {}

    public appBootstrapCompleted(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.APP_BOOTSTRAP_COMPLETED,
            ERROR_REPORTER_BREADCRUMB_TYPE.STATE,
            metadata,
        );
    }

    public jsExceptionWithResponse(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.JS_EXCEPTION_WITH_RESPONSE_PAYLOAD,
            ERROR_REPORTER_BREADCRUMB_TYPE.ERROR,
            metadata,
        );
    }

    public unhandledJsExceptionCaptured(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.UNHANDLED_JS_EXCEPTION_CAPTURED,
            ERROR_REPORTER_BREADCRUMB_TYPE.ERROR,
            metadata,
        );
    }

    public authUserLoggedOut(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.AUTH_USER_LOGGED_OUT,
            ERROR_REPORTER_BREADCRUMB_TYPE.USER,
            metadata,
        );
    }

    public authUserSessionIdentified(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.AUTH_USER_SESSION_IDENTIFIED,
            ERROR_REPORTER_BREADCRUMB_TYPE.USER,
            metadata,
        );
    }

    public navigationReady(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.NAVIGATION_READY,
            ERROR_REPORTER_BREADCRUMB_TYPE.NAVIGATION,
            metadata,
        );
    }

    public navigationRouteChanged(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.NAVIGATION_ROUTE_CHANGED,
            ERROR_REPORTER_BREADCRUMB_TYPE.NAVIGATION,
            metadata,
        );
    }

    public loginApiRequestStarted(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.LOGIN_API_REQUEST_STARTED,
            ERROR_REPORTER_BREADCRUMB_TYPE.REQUEST,
            metadata,
        );
    }

    public loginApiRequestCompleted(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.LOGIN_API_REQUEST_COMPLETED,
            ERROR_REPORTER_BREADCRUMB_TYPE.REQUEST,
            metadata,
        );
    }

    public loginEmailPasswordSubmitted(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.LOGIN_EMAIL_PASSWORD_SUBMITTED,
            ERROR_REPORTER_BREADCRUMB_TYPE.USER,
            metadata,
        );
    }

    public loginEmailPasswordSucceeded(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.LOGIN_EMAIL_PASSWORD_SUCCEEDED,
            ERROR_REPORTER_BREADCRUMB_TYPE.USER,
            metadata,
        );
    }

    public loginEmailPasswordFailed(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.LOGIN_EMAIL_PASSWORD_FAILED,
            ERROR_REPORTER_BREADCRUMB_TYPE.ERROR,
            metadata,
        );
    }

    public loginRequiresIpChallenge(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.LOGIN_REQUIRES_IP_CHALLENGE,
            ERROR_REPORTER_BREADCRUMB_TYPE.USER,
            metadata,
        );
    }

    public loginIpChallengeSubmitted(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.LOGIN_IP_CHALLENGE_SUBMITTED,
            ERROR_REPORTER_BREADCRUMB_TYPE.USER,
            metadata,
        );
    }

    public loginIpChallengeSucceeded(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.LOGIN_IP_CHALLENGE_SUCCEEDED,
            ERROR_REPORTER_BREADCRUMB_TYPE.USER,
            metadata,
        );
    }

    public loginIpChallengeFailed(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.LOGIN_IP_CHALLENGE_FAILED,
            ERROR_REPORTER_BREADCRUMB_TYPE.ERROR,
            metadata,
        );
    }

    public socialLoginSubmitted(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.SOCIAL_LOGIN_SUBMITTED,
            ERROR_REPORTER_BREADCRUMB_TYPE.USER,
            metadata,
        );
    }

    public socialLoginSucceeded(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.SOCIAL_LOGIN_SUCCEEDED,
            ERROR_REPORTER_BREADCRUMB_TYPE.USER,
            metadata,
        );
    }

    public socialLoginFailed(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.SOCIAL_LOGIN_FAILED,
            ERROR_REPORTER_BREADCRUMB_TYPE.ERROR,
            metadata,
        );
    }

    public tokenRefreshRequestQueued(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.REQUEST_QUEUED_WHILE_REFRESHING_AUTH_TOKEN,
            ERROR_REPORTER_BREADCRUMB_TYPE.PROCESS,
            metadata,
        );
    }

    public tokenRefreshStarted(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.ACCESS_TOKEN_REFRESH_STARTED,
            ERROR_REPORTER_BREADCRUMB_TYPE.PROCESS,
            metadata,
        );
    }

    public tokenRefreshSucceeded(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.ACCESS_TOKEN_REFRESH_SUCCEEDED,
            ERROR_REPORTER_BREADCRUMB_TYPE.PROCESS,
            metadata,
        );
    }

    public tokenRefreshFailed(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.ACCESS_TOKEN_REFRESH_FAILED,
            ERROR_REPORTER_BREADCRUMB_TYPE.ERROR,
            metadata,
        );
    }

    public createOrderApiRequestStarted(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.CREATE_ORDER_API_REQUEST_STARTED,
            ERROR_REPORTER_BREADCRUMB_TYPE.REQUEST,
            metadata,
        );
    }

    public createOrderApiRequestCompleted(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.CREATE_ORDER_API_REQUEST_COMPLETED,
            ERROR_REPORTER_BREADCRUMB_TYPE.REQUEST,
            metadata,
        );
    }

    public transactionCreationRequested(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.TRANSACTION_CREATION_REQUESTED,
            ERROR_REPORTER_BREADCRUMB_TYPE.PROCESS,
            metadata,
        );
    }

    public createOrderValidationFailed(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.CREATE_ORDER_VALIDATION_FAILED,
            ERROR_REPORTER_BREADCRUMB_TYPE.PROCESS,
            metadata,
        );
    }

    public createOrderRejectedByApi(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.CREATE_ORDER_REJECTED_BY_API,
            ERROR_REPORTER_BREADCRUMB_TYPE.ERROR,
            metadata,
        );
    }

    public transactionCreationFailed(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.TRANSACTION_CREATION_FAILED,
            ERROR_REPORTER_BREADCRUMB_TYPE.ERROR,
            metadata,
        );
    }

    public transactionCreatedSuccessfully(metadata: ErrorReporterMetadata): void {
        this.track(
            BREADCRUMB_NAME.TRANSACTION_CREATED_SUCCESSFULLY,
            ERROR_REPORTER_BREADCRUMB_TYPE.PROCESS,
            metadata,
        );
    }

    private track(
        message: BreadcrumbName,
        type: ErrorReporterBreadcrumbType,
        metadata: ErrorReporterMetadata,
    ): void {
        this.errorReporterService.leaveBreadcrumb(message, metadata, type);
    }
}
